import React, {FC, useState, useRef, useEffect} from 'react'
import './ColorPicker.less'

interface ColorPickerProps {
    value: string
    presets: string[]
    onChange: (color: string) => void
    mixed?: boolean
}

const isValidHex = (hex: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(hex)

export const ColorPicker: FC<ColorPickerProps> = ({
    value,
    presets,
    onChange,
    mixed = false,
}) => {
    const [hexInput, setHexInput] = useState(value)
    const [showError, setShowError] = useState(false)
    const [isPopupOpen, setIsPopupOpen] = useState(false)
    const [openUpward, setOpenUpward] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsPopupOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const openPopup = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const estimatedPopupHeight = 160
            setOpenUpward(spaceBelow < estimatedPopupHeight)
        }
        setIsPopupOpen(true)
    }

    const handlePresetClick = (event: React.MouseEvent<HTMLButtonElement>, color: string) => {
        event.preventDefault()
        event.stopPropagation()
        onChange(color)
        setHexInput(color)
        setShowError(false)
        setIsPopupOpen(false)
    }

    const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setHexInput(newValue)
        
        const formattedValue = newValue.startsWith('#') ? newValue : `#${newValue}`
        if (isValidHex(formattedValue)) {
            onChange(formattedValue)
            setShowError(false)
        } else {
            setShowError(true)
        }
    }

    const handleHexBlur = () => {
        if (!isValidHex(hexInput)) {
            setHexInput(value)
            setShowError(false)
        }
    }

    return (
        <div className='color-picker' ref={containerRef}>
            <button
                className={`color-picker__swatch color-picker__swatch--selected${mixed ? ' color-picker__swatch--mixed' : ''}`}
                style={mixed ? undefined : {backgroundColor: value}}
                onMouseDown={openPopup}
                aria-label='Select color'
                type='button'
            />
            {isPopupOpen && (
                <div className={`color-picker__popup${openUpward ? ' color-picker__popup--upward' : ''}`} ref={popupRef}>
                    <div className='color-picker__presets'>
                        {['#000000', ...presets].map((color) => (
                            <button
                                key={color}
                                className={`color-picker__swatch ${value === color ? 'color-picker__swatch--selected' : ''}`}
                                style={{backgroundColor: color}}
                                onMouseDown={e => handlePresetClick(e, color)}
                                aria-label={`Select color ${color}`}
                                type='button'
                            />
                        ))}
                    </div>
                    <div className='color-picker__hex-wrapper'>
                        <input
                            type='text'
                            className={`color-picker__hex-input ${showError ? 'color-picker__hex-input--error' : ''}`}
                            value={hexInput}
                            onChange={handleHexChange}
                            onBlur={handleHexBlur}
                            placeholder='#000000'
                            maxLength={7}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
